/**
 * **The walls** (Stream B, `Walls.dc.html`, on E02's wall mode): each its own route, no rail, no
 * chrome — a wall never has a pointer, and only the gate kiosk and the night phone have something
 * to press. Every one is drawn on the same frame — the head and its clock, a hero figure where
 * there is one, the tiles with their chip, figure, rows and source, the foot and the sync time —
 * at the size it lives at (TV · kiosk · phone), and is built to it literally (`DrawnWall`). Each
 * reads its screen's own answer once a minute: the sync time goes gold past ten minutes, and past
 * thirty the tiles read NO DATA rather than show an old number as now. A read that found nothing
 * is 0; NO DATA only where there is no source, or no answer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { formatCount, formatCountDay, formatMoney, formatTime } from '@cider/ui';
import { api } from '../../shell/api.js';
import { wallFreshness, type WallFreshness } from '../../shell/wall.js';
import { useLoad } from '../admin/shared.js';
import { fiscalYearToday } from '../money/shared.js';
import { useWtYear, type Board } from './Watchtower.js';

interface WallRead<T> {
  data: T | null;
  error: string | null;
  fresh: WallFreshness;
  syncedAt: Date | null;
}

/** A screen's answer, read once a minute, with when it last answered. */
function useWallRead<T>(path: string): WallRead<T> {
  const load = useLoad<T>(path);
  const { reload } = load;
  const [syncedAt, setSyncedAt] = useState<Date | null>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (load.data) setSyncedAt(new Date());
  }, [load.data]);
  useEffect(() => {
    const read = setInterval(reload, 60_000);
    const tick = setInterval(() => {
      setNow(new Date());
    }, 15_000);
    return () => {
      clearInterval(read);
      clearInterval(tick);
    };
  }, [reload]);
  const fresh = wallFreshness(syncedAt, now);
  return { data: fresh === 'no data' ? null : load.data, error: load.error, fresh, syncedAt };
}

type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';

/** A tile as the drawing draws it: its label and chip, a figure, rows, and where it reads from. */
interface DrawnTile {
  label: string;
  chip: string;
  tone: Tone;
  /** The top rule: red is a gap, gold a watch, sky everything else. */
  edge?: 'gap' | 'watch' | 'info';
  /** Undefined draws no figure; null draws NO DATA where the figure goes. */
  n?: string | null;
  rows?: readonly { a: string; b: string; tone?: 'bad' | 'warn' }[];
  src?: string;
  span?: 2;
}

/** The drawing's number steps down as it grows: past three characters 0.8, past five 0.62. */
const lenOf = (n: string): 'mid' | 'long' | undefined =>
  n.length > 5 ? 'long' : n.length > 3 ? 'mid' : undefined;

/** Whole dollars, as a wall reads at ten feet. */
const whole = (c: number): string => formatMoney(c).replace(/\.\d\d$/, '');

/** The festival day's name, "Saturday", from the operating day. */
const dayName = (ymd: string): string => {
  const d = formatCountDay(ymd).split(',')[0] ?? '';
  return d.charAt(0) + d.slice(1).toLowerCase();
};

/** One wall as `Walls.dc.html` draws every wall. */
function DrawnWall({
  screen,
  size,
  theme,
  head,
  clock,
  hero,
  heroSub,
  heroTone,
  cols,
  tiles,
  aside,
  action,
  foot,
  sync,
  fresh,
  error,
}: {
  screen: string;
  size: 'tv' | 'kiosk' | 'phone';
  theme: 'light' | 'dark' | 'navy';
  head: string;
  clock: string;
  hero?: string | null;
  heroSub?: string;
  heroTone?: 'ok';
  cols: '1' | '2' | '4' | '5';
  tiles: readonly DrawnTile[];
  /** Under the tiles, in the room the drawing leaves empty: what the drawing does not draw. */
  aside?: ReactNode;
  action?: ReactNode;
  foot: string;
  sync: string;
  fresh: WallFreshness;
  error?: string | null;
}): ReactNode {
  return (
    <div className="dw" data-screen={`wall-${screen}`} data-size={size} data-theme={theme}>
      <div className="dw__head">
        <h1 className="dw__title">{head}</h1>
        <span className="dw__clock">{clock}</span>
      </div>
      {error ? <p className="dw__error">{error}</p> : null}
      {hero !== undefined ? (
        <div className="dw__hero">
          <span className="dw__hero-n" data-tone={hero === null ? undefined : heroTone}>
            {hero ?? 'NO DATA'}
          </span>
          {heroSub ? <span className="dw__sub">{heroSub}</span> : null}
        </div>
      ) : null}
      <div className="dw__tiles" data-cols={cols}>
        {tiles.map((t) => (
          <section
            key={t.label}
            className="dw__tile"
            data-edge={t.edge ?? 'info'}
            data-span={t.span}
            aria-label={t.label}
          >
            <div className="dw__tile-head">
              <span className="dw__label">{t.label}</span>
              {t.chip ? (
                <span className="dw__chip" data-tone={t.tone}>
                  {t.chip}
                </span>
              ) : null}
            </div>
            {t.n !== undefined ? (
              <div
                className="dw__n"
                data-len={t.n === null ? 'long' : lenOf(t.n)}
                data-tone={t.tone === 'bad' && t.n !== null ? 'bad' : undefined}
              >
                {t.n ?? 'NO DATA'}
              </div>
            ) : null}
            {(t.rows ?? []).map((r, i) => (
              <div key={String(i)} className="dw__row">
                <span className="dw__a">{r.a}</span>
                <span className="dw__b" data-tone={r.tone}>
                  {r.b}
                </span>
              </div>
            ))}
            {t.src ? <div className="dw__src">{t.src}</div> : null}
          </section>
        ))}
        {aside ? <div className="dw__aside">{aside}</div> : null}
      </div>
      {action}
      <div className="dw__foot" data-stale={fresh === 'fresh' ? undefined : 'watch'}>
        <span>{foot}</span>
        <span>{sync}</span>
      </div>
    </div>
  );
}

const synced = (w: { syncedAt: Date | null }, source?: string): string =>
  w.syncedAt
    ? `${source ? `${source} · ` : ''}synced ${formatTime(w.syncedAt)}`
    : `${source ? `${source} · ` : ''}NO DATA`;
const clockOf = (w: { syncedAt: Date | null }): string =>
  w.syncedAt ? formatTime(w.syncedAt) : 'NO DATA';

interface MoneyWallData {
  day: string;
  pulled: boolean;
  cardCents: number | null;
  cashCountedCents: number;
  areas: { area: string; cardCents: number }[];
  registers: { register: string; countedCents: number }[];
}

interface BagsData {
  bags: { bagCode: string; status: string; bagType: string }[];
}

/** The bag machine's states, as the walls draw them: out at a register, walking, counted, deposited. */
const BAG_STATES: { label: string; chip: string; tone: Tone; states: string[]; edge?: 'watch' }[] =
  [
    { label: 'OUT', chip: 'AT A REGISTER', tone: 'info', states: ['available'] },
    { label: 'PICKED UP', chip: 'WALKING', tone: 'warn', states: ['in_transit'], edge: 'watch' },
    {
      label: 'COUNTED',
      chip: 'IN THE COUNT ROOM',
      tone: 'ok',
      states: ['received', 'counting', 'locked'],
    },
    { label: 'DEPOSITED', chip: 'CLEARED', tone: 'ok', states: ['cleared'] },
  ];
const fieldBags = (d: BagsData | null): BagsData['bags'] =>
  (d?.bags ?? []).filter((b) => b.bagType !== 'seed' && b.status !== 'retired');

/** B01 · the money wall: Square by area, cash counted by register, the bags by state, the gaps. */
export function MoneyWallTv(): ReactNode {
  const year = useWtYear();
  const w = useWallRead<MoneyWallData>(`/ops/money-wall?year=${String(year)}`);
  const b = useWallRead<BagsData>(`/money/countroom/${String(year)}/bags`);
  const d = w.data;
  const bags = fieldBags(b.data);
  const gaps = bags.filter((x) => x.status === 'exception');
  return (
    <DrawnWall
      screen="money"
      size="tv"
      theme="light"
      head={d ? `Money wall · ${dayName(d.day)}` : 'Money wall'}
      clock={`${clockOf(w)} · ${d?.pulled ? 'SQUARE PULLED' : 'SQUARE NOT PULLED'}`}
      hero={d?.pulled && d.cardCents !== null ? whole(d.cardCents) : null}
      heroSub="Square today so far"
      heroTone="ok"
      cols="4"
      error={w.error && !d ? w.error : null}
      tiles={[
        {
          label: 'SQUARE BY AREA',
          chip: d?.pulled ? 'LIVE' : 'NOT PULLED',
          tone: d?.pulled ? 'ok' : 'mute',
          rows: (d?.areas ?? []).slice(0, 5).map((a) => ({ a: a.area, b: whole(a.cardCents) })),
          src: 'Square pull · reader → area',
        },
        {
          label: 'CASH COUNTED',
          chip: 'SEALED DAYS ONLY',
          tone: 'info',
          n: d ? whole(d.cashCountedCents) : null,
          rows: (d?.registers ?? [])
            .slice(0, 5)
            .map((r) => ({ a: r.register, b: whole(r.countedCents) })),
          src: 'count sheets',
        },
        {
          label: 'BAGS BY STATE',
          chip: 'TODAY',
          tone: 'mute',
          n: b.data ? String(bags.length) : null,
          rows: [
            { a: 'Issued', b: String(bags.length) },
            ...BAG_STATES.map((s) => ({
              a: s.label.charAt(0) + s.label.slice(1).toLowerCase(),
              b: String(bags.filter((x) => s.states.includes(x.status)).length),
            })),
          ],
          src: 'the cash strip',
        },
        {
          label: 'GAPS',
          chip: 'RED = A GAP',
          tone: gaps.length ? 'bad' : 'mute',
          edge: gaps.length ? 'gap' : 'info',
          n: b.data ? String(gaps.length) : null,
          rows: gaps
            .slice(0, 4)
            .map((x) => ({ a: `Bag ${x.bagCode}`, b: 'exception', tone: 'bad' })),
          src: 'the bag registry',
        },
      ]}
      foot={`Apple Fest ${String(year)}`}
      sync={synced(w)}
      fresh={w.fresh}
    />
  );
}

/** B04 · the count-room wall: every field bag across the machine's states, an exception in red. */
export function CountRoomWall(): ReactNode {
  const year = useWtYear();
  const w = useWallRead<BagsData>(`/money/countroom/${String(year)}/bags`);
  const bags = fieldBags(w.data);
  const gaps = bags.filter((x) => x.status === 'exception');
  const list = (mine: BagsData['bags'], word: string): { a: string; b: string }[] => [
    ...mine.slice(0, 3).map((x) => ({ a: x.bagCode, b: word })),
    ...(mine.length > 3 ? [{ a: `+ ${String(mine.length - 3)}`, b: '' }] : []),
  ];
  return (
    <DrawnWall
      screen="count-room"
      size="tv"
      theme="light"
      head="Bags"
      clock={clockOf(w)}
      cols="5"
      error={w.error && !w.data ? w.error : null}
      tiles={[
        {
          label: 'ISSUED',
          chip: '',
          tone: 'mute',
          n: w.data ? String(bags.length) : null,
          rows: list(bags, 'issued'),
        },
        ...BAG_STATES.map((s, i): DrawnTile => {
          const mine = bags.filter((x) => s.states.includes(x.status));
          const last = i === BAG_STATES.length - 1;
          return {
            label: s.label,
            chip: s.chip,
            tone: s.tone,
            edge: last && gaps.length ? 'gap' : (s.edge ?? 'info'),
            n: w.data ? String(mine.length) : null,
            rows: [
              ...list(mine, s.label.toLowerCase()),
              // An exception is a gap: it is read in red on the last column, as drawn.
              ...(last
                ? gaps.map((x) => ({ a: x.bagCode, b: 'EXCEPTION', tone: 'bad' as const }))
                : []),
            ],
          };
        }),
      ]}
      foot="Count-room wall · a bag in red has a gap or a missing state"
      sync={synced(w, 'the bag registry')}
      fresh={w.fresh}
    />
  );
}

interface UnreturnedLine {
  item: string;
}

/** Decide's money rows, as the finance director's worklist serves them (ADR 0036). */
interface WorkRow {
  id: string;
  kind: 'expense' | 'bill' | 'po' | 'line';
  what: string;
  line: string;
  cents: number;
}
const WORK_KIND: Record<WorkRow['kind'], string> = {
  expense: 'I spent',
  bill: 'Bill',
  po: 'PO',
  line: 'Over the line',
};

/** The incidents still open, as the night phone and the directors' board read them. */
function openIncidents(d: Board | null): Board['incidents']['rows'] {
  return (d?.incidents.rows ?? []).filter((i) => i.state !== 'closed');
}

/** B06 · the directors' board (wt09): the festival day's figures, read-only, on a TV or a phone. */
export function DirectorsWall(): ReactNode {
  const year = useWtYear();
  const w = useWallRead<Board>(`/ops/board?year=${String(year)}`);
  const m = useWallRead<MoneyWallData>(`/ops/money-wall?year=${String(year)}`);
  const radios = useWallRead<UnreturnedLine[]>(`/ops/custody/unreturned?year=${String(year)}`);
  const work = useWallRead<{ submitted: WorkRow[]; redLines: WorkRow[] }>(
    `/money/worklist/${String(fiscalYearToday())}`,
  );
  const waiting = work.data ? [...work.data.submitted, ...work.data.redLines] : null;
  const d = w.data;
  const money = m.data;
  const open = openIncidents(d);
  const hold = d?.weather.hold;
  const radiosOut = radios.data ? radios.data.filter((r) => r.item === 'radio').length : null;
  return (
    <DrawnWall
      screen="directors"
      size="tv"
      theme="light"
      head={d?.day ? `Directors’ board · ${dayName(d.day)}` : 'Directors’ board'}
      clock={clockOf(w)}
      cols="4"
      error={w.error && !d ? w.error : null}
      tiles={[
        {
          label: 'RECEIPTS TODAY',
          chip: money?.pulled ? 'SQUARE' : 'NOT PULLED',
          tone: money?.pulled ? 'ok' : 'mute',
          n: money?.pulled && money.cardCents !== null ? whole(money.cardCents) : null,
          rows: [{ a: 'Cash counted', b: money ? whole(money.cashCountedCents) : 'NO DATA' }],
          src: 'the money wall',
        },
        {
          label: 'DECIDE · MONEY',
          chip: !waiting ? 'NO DATA' : waiting.length ? 'AWAITING A DIRECTOR' : 'NOTHING WAITING',
          tone: waiting?.length ? 'warn' : waiting ? 'ok' : 'mute',
          edge: waiting?.length ? 'watch' : 'info',
          n: waiting ? String(waiting.length) : null,
          // Each one as Decide says it: the kind, the amount and what for; who it is for.
          rows: (waiting ?? []).slice(0, 3).map((r) => ({
            a: `${WORK_KIND[r.kind]} · ${whole(r.cents)} · ${r.what}`,
            b: r.line.split(',')[0] ?? '',
          })),
          src: 'Decide',
        },
        {
          label: 'INCIDENTS OPEN',
          chip: open[0] ? open[0].kindWords.toUpperCase() : d ? 'NONE OPEN' : 'NO DATA',
          tone: open.length ? 'warn' : d ? 'ok' : 'mute',
          edge: open.length ? 'watch' : 'info',
          n: d ? String(open.length) : null,
          rows: open.slice(0, 3).map((i) => ({
            a: `${i.place} · ${formatTime(new Date(i.openedAt))}`,
            b: i.going ?? 'nobody yet',
          })),
          src: 'Incidents',
        },
        {
          label: 'WEATHER',
          chip: !hold ? 'NO DATA' : hold.held ? 'HOLDING' : 'NO HOLD',
          tone: hold?.held ? 'warn' : hold ? 'ok' : 'mute',
          edge: hold?.held ? 'watch' : 'info',
          rows: [
            {
              a: d?.weather.forecast?.periods[0]?.text.split('. ')[0] ?? 'The forecast',
              b:
                hold?.lastStrike?.miles != null
                  ? `last strike ${String(hold.lastStrike.miles)} mi`
                  : '',
            },
          ],
          src: 'Weather console',
        },
        {
          label: 'BY AREA · SQUARE',
          chip: money?.pulled ? 'LIVE' : 'NOT PULLED',
          tone: money?.pulled ? 'ok' : 'mute',
          rows: (money?.areas ?? []).slice(0, 4).map((a) => ({ a: a.area, b: whole(a.cardCents) })),
          src: 'the money wall’s strip',
          span: 2,
        },
        {
          label: 'ON SITE · ROLE HOLDERS',
          chip: '',
          tone: 'mute',
          n: d?.numbers.onSite != null ? String(d.numbers.onSite) : null,
          rows: [
            { a: 'By role', b: 'NO DATA' },
            { a: 'Radios out', b: radiosOut === null ? 'NO DATA' : String(radiosOut) },
          ],
          src: 'the I’m here log',
        },
        {
          label: 'GAPS BELOW MINIMUM',
          chip: 'NO DATA',
          tone: 'mute',
          n: null,
          rows: [],
          src: 'rosters · check-ins',
        },
      ]}
      foot="Directors’ board · read-only · the phone shows the same tiles stacked"
      sync={synced(w)}
      fresh={w.fresh}
    />
  );
}

interface StockData {
  rows: {
    id: string;
    name: string;
    areaName: string | null;
    threshold: number;
    onHand: number;
    state: 'ABOVE THRESHOLD' | 'BELOW THRESHOLD' | 'NO THRESHOLD';
  }[];
}

/**
 * B02 · the kitchen board: hung over the pass, from Supplies — what is below its par for the area
 * the address names (`/wall/kitchen?area=Food tent`). Runs coming, what is 86'd and the next
 * delivery have no source in CIDER yet: those tiles say NO DATA.
 */
export function KitchenWall(): ReactNode {
  const year = useWtYear();
  const area = new URLSearchParams(window.location.search).get('area');
  const w = useWallRead<StockData>(`/ops/stock-on-hand?year=${String(year)}`);
  const rows = (w.data?.rows ?? []).filter(
    (r) => !area || (r.areaName ?? '').toLowerCase() === area.toLowerCase(),
  );
  const low = rows.filter((r) => r.state === 'BELOW THRESHOLD');
  const none = (label: string, src: string): DrawnTile => ({
    label,
    chip: 'NO DATA',
    tone: 'mute',
    n: null,
    rows: [],
    src,
  });
  return (
    <DrawnWall
      screen="kitchen"
      size="tv"
      theme="light"
      head={area ? `Kitchen · ${area}` : 'Kitchen'}
      clock={clockOf(w)}
      cols="4"
      error={w.error && !w.data ? w.error : null}
      tiles={[
        {
          label: 'BELOW PAR',
          chip: low.length ? 'ORDER NOW' : 'ALL AT PAR',
          tone: low.length ? 'bad' : 'ok',
          edge: low.length ? 'gap' : 'info',
          n: w.data ? String(low.length) : null,
          rows: low.slice(0, 5).map((r) => ({
            a: r.name,
            b: `${String(r.onHand)} of ${String(r.threshold)}`,
            tone: 'bad',
          })),
          src: 'par levels · on hand',
        },
        none('RUNS COMING', 'the shopping list'),
        none('86’D', 'the chair’s phone'),
        none('NEXT DELIVERY', 'the procurement calendar'),
      ]}
      foot={area ? `Kitchen board · ${area}` : 'Kitchen board'}
      sync={synced(w, 'Supplies')}
      fresh={w.fresh}
    />
  );
}

interface GateCountData {
  gate: string;
  day: string;
  hour: string;
  thisHour: number;
  today: number;
  byHour: { hour: string; count: number }[];
}

/**
 * B03 · the gate kiosk (`/wall/gate?gate=Main gate`): the gate's name, one big number this hour,
 * today, and a button the size of a hand — one tap, one person in. The hour rolls over on its own;
 * a mis-tap is taken back by a −1 row, never an edit.
 */
export function GateKiosk(): ReactNode {
  const year = useWtYear();
  const gate = new URLSearchParams(window.location.search).get('gate') ?? '';
  const w = useWallRead<GateCountData>(
    gate
      ? `/ops/gate-count?year=${String(year)}&gate=${encodeURIComponent(gate)}`
      : '/ops/gate-count?year=0&gate=-',
  );
  const [live, setLive] = useState<GateCountData | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const d = live ?? w.data;
  const tap = (count: 1 | -1): void => {
    setBusy(true);
    api<GateCountData>('POST', '/ops/gate-count', { year, gate, count })
      .then((r) => {
        setLive(r);
        setSaid(null);
      })
      .catch((e: unknown) => {
        setSaid(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  if (!gate)
    return (
      <DrawnWall
        screen="gate"
        size="kiosk"
        theme="navy"
        head="Which gate?"
        clock=""
        cols="1"
        tiles={[]}
        error="Open this screen as /wall/gate?gate= and the gate’s name."
        foot=""
        sync=""
        fresh="fresh"
      />
    );
  return (
    <DrawnWall
      screen="gate"
      size="kiosk"
      theme="navy"
      head={gate}
      clock={d ? `${dayName(d.day)} · ${d.hour} hour` : 'This hour'}
      // Until the first read lands the number says so and the button shows a dash (James, Oct 19):
      // a tap still counts, but nobody taps into a blank.
      hero={d ? formatCount(d.thisHour) : 'loading'}
      heroSub={d ? `in this hour · today ${formatCount(d.today)}` : ''}
      cols="2"
      tiles={[
        {
          label: 'THIS HOUR',
          chip: d ? d.hour : '',
          tone: 'info',
          n: d ? formatCount(d.thisHour) : null,
        },
        {
          label: 'TODAY',
          chip: gate.toUpperCase(),
          tone: 'mute',
          n: d ? formatCount(d.today) : null,
          rows: (d?.byHour ?? []).slice(0, 3).map((h) => ({ a: h.hour, b: formatCount(h.count) })),
        },
      ]}
      // The undo is not drawn: it waits in the room under the tiles, and the drawn button keeps its place.
      aside={
        <>
          <button
            type="button"
            className="dw__undo"
            disabled={busy}
            onClick={() => {
              tap(-1);
            }}
          >
            Take one back
          </button>
          {said ? (
            <p className="dw__error" role="alert">
              {said}
            </p>
          ) : null}
        </>
      }
      action={
        <button
          type="button"
          className="dw__act"
          aria-label="Tap · one person in"
          disabled={busy}
          onClick={() => {
            tap(1);
          }}
        >
          {d ? 'Tap · one person in' : '—'}
        </button>
      }
      foot={gate}
      sync={synced(w)}
      fresh={w.fresh}
    />
  );
}

/**
 * B05 · the watchtower at night (`/wall/night`, PHONE · 390 · DARK): dispatch after sunset on a
 * phone under a tent light — the incidents still open, the weather's state, who is on site and the
 * radios out, and one way to report an incident. Always dark, whatever the hour: it is the night's
 * screen. Who is on site by role has no source in CIDER yet; that row says NO DATA.
 */
export function NightPhone(): ReactNode {
  const year = useWtYear();
  const w = useWallRead<Board>(`/ops/board?year=${String(year)}`);
  const radios = useWallRead<UnreturnedLine[]>(`/ops/custody/unreturned?year=${String(year)}`);
  useEffect(() => {
    const root = document.documentElement;
    const was = root.dataset['fieldTheme'];
    root.dataset['fieldTheme'] = 'dark';
    return () => {
      if (was === undefined) delete root.dataset['fieldTheme'];
      else root.dataset['fieldTheme'] = was;
    };
  }, []);
  const d = w.data;
  const open = openIncidents(d);
  const first = open[0];
  const hold = d?.weather.hold;
  const period = d?.weather.forecast?.periods[0];
  const radiosOut = radios.data ? radios.data.filter((r) => r.item === 'radio').length : null;
  const day = d?.day ? formatCountDay(d.day).slice(0, 3) : null;
  return (
    <DrawnWall
      screen="night"
      size="phone"
      theme="dark"
      head="Dispatch"
      clock={w.syncedAt ? `${formatTime(w.syncedAt)}${day ? ` · ${day}` : ''}` : 'NO DATA'}
      cols="1"
      error={w.error && !d ? w.error : null}
      tiles={[
        {
          label: 'INCIDENTS OPEN',
          chip: !d
            ? 'NO DATA'
            : first
              ? `${first.kindWords} · ${first.going ? 'someone going' : 'nobody yet'}`.toUpperCase()
              : 'NONE OPEN',
          tone: first ? 'warn' : d ? 'ok' : 'mute',
          edge: first ? 'watch' : 'info',
          n: d ? String(open.length) : null,
          // Each open one: what and where, when it was opened; then who is going, by name.
          rows: open
            .slice(0, 2)
            .flatMap((i) => [
              { a: `${i.what} · ${i.place}`, b: formatTime(new Date(i.openedAt)) },
              ...(i.going ? [{ a: i.report ?? 'Going', b: `${i.going} on it` }] : []),
            ]),
        },
        {
          label: 'WEATHER',
          chip: !hold
            ? 'NO DATA'
            : hold.held
              ? `HOLDING${hold.lastStrike?.miles != null ? ` · ${String(hold.lastStrike.miles)} MI` : ''}`
              : 'NO HOLD',
          tone: hold?.held ? 'warn' : hold ? 'ok' : 'mute',
          edge: hold?.held ? 'watch' : 'info',
          rows: [
            ...(hold?.held && hold.heldBy
              ? [
                  {
                    a: `Held by ${hold.heldBy}`,
                    b: hold.minutesLeft != null ? `${String(hold.minutesLeft)} min left` : '',
                  },
                ]
              : []),
            period
              ? { a: period.text.split('. ')[0] ?? period.text, b: '' }
              : { a: d?.weather.forecastSays ?? 'The forecast', b: d ? '' : 'NO DATA' },
          ],
        },
        {
          label: 'ON SITE · ROLE HOLDERS',
          chip: radiosOut === null ? 'RADIOS · NO DATA' : `RADIOS OUT ${String(radiosOut)}`,
          tone: 'mute',
          n: d?.numbers.onSite != null ? String(d.numbers.onSite) : null,
          rows: [
            {
              a: 'Checked in today',
              b: d?.numbers.checkedInToday != null ? String(d.numbers.checkedInToday) : 'NO DATA',
            },
            { a: 'By role', b: 'NO DATA' },
          ],
        },
      ]}
      action={
        <a className="dw__act" href="/ops/incidents-and-weather/incidents">
          Report an incident
        </a>
      }
      foot="Field app · night"
      sync={w.syncedAt ? `synced ${formatTime(w.syncedAt)}` : 'NO DATA'}
      fresh={w.fresh}
    />
  );
}
